import { createDateFromDDMMYYYY, createDateFromYYYYMM } from "@icarius-utils/date";
import { useState } from "react";
import * as yup from 'yup';
import { useDispatch } from "react-redux";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { formatDateYYYYMMDD, formatDateYYYYMM } from "@icarius-utils/date";
import { getLocalizedString } from "@icarius-localization/strings";

const useHandleForm = (data, createCallback, modifyCallback, entryStructureCode, invalidDataCallback, gridRef, hasThirdPartyPayment) => {
  const dispatch = useDispatch();

  const isCreate = !Boolean(data["Total del préstamo"]);

  const createInitialData = () => {

    const getInitialFieldData = (fieldName) => {
      if (fieldName === 'Fecha del préstamo') {
        return (isCreate || !data[fieldName]) ? new Date() : createDateFromDDMMYYYY(data[fieldName]);
      }

      if (fieldName === 'Período de inicio del descuento') {
        return (isCreate || !data[fieldName]) ? new Date() : createDateFromYYYYMM(data[fieldName]);
      }

      return isCreate &&
        fieldName !== "Código de empleado" &&
        fieldName !== "Apellido y nombres" &&
        fieldName !== "Frecuencia del descuento" &&
        fieldName !== "Número de cuota de referencia" &&
        fieldName !== "Cuotas pagadas" &&
        fieldName !== "Valor pagado" &&
        fieldName !== "Cuotas pendientes" &&
        fieldName !== "Saldo pendiente"
        ? "" : data[fieldName];
    }

    const commonFieldNames = [
      "Código de empleado",
      "Apellido y nombres",
      "Concepto",
      "Nombre del concepto",
      "Fecha del préstamo",
      "Total del préstamo",
      "Moneda",
      "Cantidad de cuotas",
      "Frecuencia del descuento",
      "Período de inicio del descuento",
      "Número de cuota de referencia",
      "Valor pagado",
      "Saldo pendiente",
      "Cuotas pagadas",
      "Cuotas pendientes"
    ];

    const thirdPartyFieldNames = [
      "Tercero",
      "Forma de pago",
      "Tipo de pago",
      "Banco",
      "Cuenta bancaria",
      "Comentarios del pago al tercero"
    ];

    const fieldNames = hasThirdPartyPayment ? [...commonFieldNames, ...thirdPartyFieldNames] : commonFieldNames;

    return fieldNames.reduce((initialData, fieldName) => {
      return ({
        ...initialData,
        [fieldName]: getInitialFieldData(fieldName)
      })
    }, {});
  }

  const [formData, setFormData] = useState(createInitialData());

  const dataIsValid = async () => {
    let commonFields = {
      "Código de empleado": yup.string().required(),
      "Apellido y nombres": yup.string().required(),
      "Concepto": yup.string().required(),
      "Nombre del concepto": yup.string().required(),
      "Fecha del préstamo": yup.string().required(),
      "Total del préstamo": yup.string().required(),
      "Moneda": yup.string().required(),
      "Cantidad de cuotas": yup.string().min(1).max(3).required(),
      "Frecuencia del descuento": yup.string().required(),
      "Período de inicio del descuento": yup.string().required(),
      "Número de cuota de referencia": yup.string().min(1).max(3).required(),
      "Valor pagado": yup.string().required(),
      "Saldo pendiente": yup.string().required(),
      "Cuotas pagadas": yup.string().required(),
      "Cuotas pendientes": yup.string().required(),
    };

    let thirdPartyFields = {
      "Tercero": yup.string().required(),
      "Forma de pago": yup.string().required(),
      "Tipo de pago": yup.string().required(),
      "Banco": yup.string().optional(),
      "Cuenta bancaria": yup.string().optional(),
      "Comentarios del pago al tercero": yup.string().optional(),
    };

    const fields = hasThirdPartyPayment ? { ...commonFields, ...thirdPartyFields } : commonFields;
    let schema = yup.object().shape(fields);
    return await schema.isValid(formData).then((valid) => valid);
  }

  const submit = async () => {
    if (isNaN(formData["Cantidad de cuotas"])) {
      dispatch(openSnackbarAction({ msg: getLocalizedString("invalidInstallmentQuantity"), severity: "error" }));
      return;
    }

    if (isNaN(formData["Número de cuota de referencia"])) {
      dispatch(openSnackbarAction({ msg: getLocalizedString("invalidInstallmentQuotaNumber"), severity: "error" }));
      return;
    }

    const gridReference = gridRef.current;

    let ds = gridReference.getDataSource();
    let filter = gridReference.getCombinedFilter();

    gridReference.saveEditData().done(resp => {
      ds.store().load({ sort: ds.sort(), filter: filter ? filter : null })
        .done((allData) => {
          if (formData["Forma de pago"] === "D" && (formData["Banco"] === "" || formData["Cuenta bancaria"] === "")) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("bankAndBankAccountFieldsCannotBeEmpty"), severity: "error" }));
            return;
          }
          if (allData.length === 0) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("emptyTableEntryByInstallments"), severity: "error" }));
            return;
          }
          gridReference.getController("validating").validate(true).then(async isValid => {
            if (isValid) {
              // Me tengo que fijar si esta bien la sumatoria
              const pendingInstallmentsValue = allData.reduce(function (acc, obj) {
                const val = obj["Valor cuota"];
                const parsedVal = typeof val === 'string' ? parseFloat(val.replace(/\./g, '').replace(/,/g, '.')) : parseFloat(val);
                return obj["Estado"] === "P" ? acc + (parsedVal || 0) : acc
              }, 0) || 0;
              const pending = parseFloat(formData["Saldo pendiente"] || 0);

              if (Math.round(pendingInstallmentsValue) !== Math.round(pending)) {
                dispatch(openSnackbarAction({ msg: getLocalizedString("installmentSumErrorPendingInstallments"), severity: "error" }));
                return;
              }

              if (await dataIsValid()) {
                let dataToSend = { ...formData, detail: allData, entryStructure: entryStructureCode };

                if (!isCreate) {
                  dataToSend = { ...dataToSend, internalCode: data["Código interno del registro"] };
                }
                dataToSend["Frecuencia del descuento"] = dataToSend["Frecuencia del descuento"] === "CP" ? "O" : "C";
                dataToSend["Número de cuota de referencia"] = String(dataToSend["Número de cuota de referencia"]);
                if (dataToSend["Número de cuota de referencia"] === "0") {
                  dataToSend["Número de cuota de referencia"] = "1";
                }
                dataToSend["Fecha del préstamo"] = formatDateYYYYMMDD(dataToSend["Fecha del préstamo"]);
                dataToSend["Período de inicio del descuento"] = dataToSend["Período de inicio del descuento"] instanceof Date ?
                  formatDateYYYYMM(dataToSend["Período de inicio del descuento"]) : dataToSend["Período de inicio del descuento"];
                dataToSend.detail = dataToSend.detail.map(row => {
                  const stringInstallmentNumber = String(row["Cuota"])
                  const val = row["Valor cuota"];
                  const loan = typeof val === 'string' ? val.replace(/\./g, '').replace(/,/g, '.') : String(val);
                  return { ...row, "Cuota": stringInstallmentNumber, "Valor cuota": loan }
                })
                isCreate ? createCallback(dataToSend) : modifyCallback(dataToSend);
              } else {
                invalidDataCallback();
                return false;
              }
            } else {
              dispatch(openSnackbarAction({ msg: getLocalizedString("checkCellForValidValues"), severity: "error" }));
            }
          })
        });
    });


  }

  const formatValue = (value, fieldName) => {
    if (
      fieldName === "Total del préstamo"
    ) {
      return value.length > 0 ? value.replace(/-/g, "") : "";
    }

    if (fieldName === "Cantidad de cuotas" ||
      fieldName === "Número de cuota de referencia") {
      return value.length > 0 ? value.substring(0, 3) : "";
    }

    return value;
  }

  const setFormValue = (value, fieldName) => {
    setFormData({
      ...formData,
      [fieldName]: formatValue(value, fieldName)
    })
  }
  const setCalculatedFieldsValues = (data) => {
    setFormData({
      ...formData,
      "Valor pagado": data[0],
      "Saldo pendiente": data[1],
      "Cuotas pagadas": data[2],
      "Cuotas pendientes": data[3]
    })
  }

  const setFormValueLoanDatePeriod = (date, period) => {
    setFormData({
      ...formData,
      "Fecha del préstamo": date,
      "Período de inicio del descuento": period
    })
  }

  const setConcept = (concept, conceptsArray) => {
    const element = conceptsArray.find(el => el.key === concept);
    setFormData({
      ...formData,
      "Concepto": concept,
      "Nombre del concepto": element.value
    })
  }

  const setPaymenthMethodValue = (value) => {
    if (value === "D") {
      setFormData({
        ...formData,
        "Forma de pago": value
      })
    } else {
      setFormData({
        ...formData,
        "Forma de pago": value,
        "Banco": "",
        "Cuenta bancaria": "",
      })
    }
  }


  return { isCreate, formData, setFormValue, setCalculatedFieldsValues, setConcept, setFormValueLoanDatePeriod, setPaymenthMethodValue, submit };
}

export default useHandleForm;
